import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import listeningService from '@/services/listening/listeningService';
import {
    fetchListeningContentRequest,
    fetchListeningContentSuccess,
    fetchListeningContentFailure,
    startListeningSessionRequest,
    startListeningSessionSuccess,
    startListeningSessionFailure,
    createListeningSessionRequest,
    createListeningSessionSuccess,
    createListeningSessionFailure,
    completeListeningSessionRequest,
    completeListeningSessionSuccess,
    completeListeningSessionFailure,
    submitListeningAnswerRequest,
    submitListeningAnswerSuccess,
    submitListeningAnswerFailure,
    uploadListeningAudioRequest,
    uploadListeningAudioSuccess,
    uploadListeningAudioFailure,
    fetchListeningFeedbackRequest,
    fetchListeningFeedbackSuccess,
    fetchListeningFeedbackFailure,
    fetchListeningContentsSuccess,
    fetchListeningContentsFailure,
    fetchListeningContentsRequest,
} from '../slices/listening/listeningSlice';
import { ContentFilters } from '@/services/listening/types';

function* fetchListeningContentsSaga(action: PayloadAction<ContentFilters | undefined>): SagaIterator {
    try {
        const response = yield call(listeningService.getListeningContents, action.payload);
        yield put(fetchListeningContentsSuccess(response));
    } catch (error: any) {
        yield put(fetchListeningContentsFailure(error.message || 'Failed to fetch listening content'));
    }
}

// Content Management
function* fetchListeningContentSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(listeningService.getListeningContent, action.payload);
        yield put(fetchListeningContentSuccess(response));
    } catch (error: any) {
        yield put(fetchListeningContentFailure(error.message || 'Failed to fetch listening content'));
    }
}

// Session Management
function* startListeningSessionSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(listeningService.startListeningSession, action.payload);
        yield put(startListeningSessionSuccess(response));
    } catch (error: any) {
        yield put(startListeningSessionFailure(error.message || 'Failed to start listening session'));
    }
}

// Session Creation
function* createListeningSessionSaga(action: PayloadAction<{ contentId: string; config?: any }>): SagaIterator {
    try {
        const response = yield call(
            listeningService.createListeningSession,
            action.payload.contentId,
            action.payload.config || {}
        );
        yield put(createListeningSessionSuccess(response));
    } catch (error: any) {
        yield put(createListeningSessionFailure(error.message || 'Failed to create listening session'));
    }
}

// Session Completion
function* completeListeningSessionSaga(action: PayloadAction<{ sessionId: string; answers?: any[] }>): SagaIterator {
    try {
        const response = yield call(
            listeningService.completeListeningSession,
            action.payload.sessionId,
            action.payload.answers || []
        );
        yield put(completeListeningSessionSuccess(response));
    } catch (error: any) {
        yield put(completeListeningSessionFailure(error.message || 'Failed to complete listening session'));
    }
}

// Answer Submission
function* submitListeningAnswerSaga(action: PayloadAction<any>): SagaIterator {
    try {
        const response = yield call(
            listeningService.submitListeningAnswer,
            action.payload.sessionId,
            action.payload.data,
        );
        yield put(submitListeningAnswerSuccess(response));
    } catch (error: any) {
        yield put(submitListeningAnswerFailure(error.message || 'Failed to submit answer'));
    }
}

// Audio Upload
function* uploadListeningAudioSaga(action: PayloadAction<{ sessionId: string; audioBlob: Blob }>): SagaIterator {
    try {
        const response = yield call(listeningService.uploadAudio, action.payload.sessionId, action.payload.audioBlob);
        yield put(uploadListeningAudioSuccess(response));
    } catch (error: any) {
        yield put(uploadListeningAudioFailure(error.message || 'Failed to upload audio'));
    }
}

// Feedback
function* fetchListeningFeedbackSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(listeningService.getSessionFeedback, action.payload);
        yield put(fetchListeningFeedbackSuccess(response));
    } catch (error: any) {
        yield put(fetchListeningFeedbackFailure(error.message || 'Failed to fetch feedback'));
    }
}

// Root Saga
export default function* listeningSaga(): SagaIterator {
    yield takeLatest(fetchListeningContentsRequest.type, fetchListeningContentsSaga);
    yield takeLatest(fetchListeningContentRequest.type, fetchListeningContentSaga);
    yield takeLatest(startListeningSessionRequest.type, startListeningSessionSaga);
    yield takeLatest(createListeningSessionRequest.type, createListeningSessionSaga);
    yield takeLatest(completeListeningSessionRequest.type, completeListeningSessionSaga);
    yield takeLatest(submitListeningAnswerRequest.type, submitListeningAnswerSaga);
    yield takeLatest(uploadListeningAudioRequest.type, uploadListeningAudioSaga);
    yield takeLatest(fetchListeningFeedbackRequest.type, fetchListeningFeedbackSaga);
}
